import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/utils";

/**
 * `Banner` is a dismissible inline status strip — heavier than {@link Alert}, which has no dismiss
 * control, and lighter than a toast because it renders inline in a form or page section. Use it for
 * messages that should stay until acknowledged; for passive ones, use Alert. Colours match the
 * historical `FormBanner` so the migration is visually a no-op.
 */
// Body text is `text-foreground`, never the variant's own color token: `text-success` on
// `bg-success/15` fails axe WCAG 2 AA contrast in light mode. The border, background and icon
// carry the variant identity instead, so the colored signal survives without the contrast failure.
const bannerVariants = cva(
  "flex items-center justify-between rounded-md border px-4 py-2.5 text-sm font-medium text-foreground",
  {
    variants: {
      variant: {
        success: "border-success/40 bg-success/15",
        error: "border-destructive/40 bg-destructive/15",
        warning: "border-warning/40 bg-warning/15",
        info: "border-border bg-muted/40",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
);

export interface BannerProps
  extends
    Omit<React.OutputHTMLAttributes<HTMLOutputElement>, "children">,
    VariantProps<typeof bannerVariants> {
  children: React.ReactNode;
  onDismiss?: () => void;
}

export function Banner({ variant, className, children, onDismiss, ...props }: BannerProps) {
  return (
    <output className={cn(bannerVariants({ variant }), className)} {...props}>
      <span>{children}</span>
      {onDismiss && (
        <button
          type="button"
          className="cursor-pointer border-none bg-transparent p-0 px-1 text-lg leading-none text-inherit opacity-70 hover:opacity-100"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </output>
  );
}

export { bannerVariants };
